/**
 * Geometria pływającego okna rozmowy — czysta matematyka, bez Reacta, żeby dało
 * się ją sprawdzić testem i żeby przeciąganie liczyło się bez re-renderów.
 */

export interface FloatRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Poniżej tych rozmiarów czat przestaje być używalny (pasek + treść + pole). */
export const FLOAT_MIN_W = 340;
export const FLOAT_MIN_H = 280;

/** Ile pikseli od krawędzi ekranu łapie przyciąganie (jak Snap w Windows). */
export const SNAP_EDGE = 28;

/** Okno musi w całości mieścić się w oknie przeglądarki — po zmianie rozmiaru
 * ekranu albo przy odczycie zapamiętanej pozycji z większego monitora. */
export function clampRect(r: FloatRect, vw: number, vh: number): FloatRect {
  const w = Math.min(Math.max(r.w, FLOAT_MIN_W), Math.max(FLOAT_MIN_W, vw));
  const h = Math.min(Math.max(r.h, FLOAT_MIN_H), Math.max(FLOAT_MIN_H, vh));
  return {
    w,
    h,
    x: Math.min(Math.max(r.x, 0), Math.max(0, vw - w)),
    y: Math.min(Math.max(r.y, 0), Math.max(0, vh - h)),
  };
}

/** Pierwsze otwarcie: prawy dolny róg, z marginesem — nie zasłania nawigacji. */
export function defaultRect(vw: number, vh: number): FloatRect {
  const w = Math.min(440, Math.max(FLOAT_MIN_W, vw - 48));
  const h = Math.min(620, Math.max(FLOAT_MIN_H, vh - 120));
  return clampRect({ w, h, x: vw - w - 24, y: vh - h - 24 }, vw, vh);
}

export type SnapZone = "left" | "right" | "top" | null;

/** Strefa przyciągania dla kursora na krawędzi ekranu (null = bez przyciągania). */
export function snapZoneFor(
  px: number,
  py: number,
  vw: number,
  edge: number = SNAP_EDGE,
): SnapZone {
  if (py <= edge) return "top";
  if (px <= edge) return "left";
  if (px >= vw - edge) return "right";
  return null;
}

/** Docelowy prostokąt dla strefy: pół ekranu z boku, cały ekran u góry. */
export function rectForZone(zone: Exclude<SnapZone, null>, vw: number, vh: number): FloatRect {
  const half = Math.max(FLOAT_MIN_W, Math.round(vw / 2));
  if (zone === "top") return clampRect({ x: 0, y: 0, w: vw, h: vh }, vw, vh);
  if (zone === "left") return clampRect({ x: 0, y: 0, w: half, h: vh }, vw, vh);
  return clampRect({ x: vw - half, y: 0, w: half, h: vh }, vw, vh);
}

/** Kierunki chwytu przy skalowaniu ("n" = góra, "se" = prawy dolny róg…). */
export type ResizeDir = "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";

/** Nowy prostokąt po przeciągnięciu chwytu o (dx, dy). Krawędź przeciwna do
 * chwytu stoi w miejscu — okno rośnie w tę stronę, w którą ciągniesz. */
export function resizeRect(
  start: FloatRect,
  dir: ResizeDir,
  dx: number,
  dy: number,
  vw: number,
  vh: number,
): FloatRect {
  let { x, y, w, h } = start;
  if (dir.includes("e")) w = start.w + dx;
  if (dir.includes("s")) h = start.h + dy;
  if (dir.includes("w")) {
    w = start.w - dx;
    x = start.x + dx;
  }
  if (dir.includes("n")) {
    h = start.h - dy;
    y = start.y + dy;
  }
  // Kurczenie poniżej minimum nie może przesuwać przeciwnej krawędzi.
  if (w < FLOAT_MIN_W && dir.includes("w")) x = start.x + start.w - FLOAT_MIN_W;
  if (h < FLOAT_MIN_H && dir.includes("n")) y = start.y + start.h - FLOAT_MIN_H;
  return clampRect({ x, y, w, h }, vw, vh);
}
